'use client'

import { useEffect } from 'react'

/**
 * Registers read-only tools with the WebMCP browser API (navigator.modelContext)
 * so agentic browsers can query the site without scraping the DOM.
 *
 * All tools are read-only by design. Nothing here writes, submits or sends.
 */

type ToolResult = { content: Array<{ type: 'text'; text: string }> }

type ToolDescriptor = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  execute: () => Promise<ToolResult>
}

type ModelContext = {
  registerTool: (tool: ToolDescriptor) => unknown
}

const NO_INPUT = { type: 'object', properties: {}, additionalProperties: false }

const text = (value: unknown): ToolResult => ({
  content: [{ type: 'text', text: typeof value === 'string' ? value : JSON.stringify(value, null, 2) }],
})

const SERVICES = [
  'GA4 Implementation & Audit: setup, custom event and conversion tracking, data quality audits, enhanced ecommerce.',
  'Google Tag Manager Implementation: container setup, data layer implementation, trigger and variable optimization, QA.',
  'Custom Analytics Dashboards: Looker Studio and Power BI, SQL-powered reports, automated delivery.',
  'GDPR & Privacy Compliance: compliance audits, consent management, cookie policy, data retention, privacy-first tracking.',
  'Analytics Consulting & Strategy: measurement roadmaps, KPI definition, AI traffic detection and measurement.',
  'Ongoing Analytics Support: monthly health checks, new event tracking, training and documentation.',
]

const TOOLS: ToolDescriptor[] = [
  {
    name: 'get_contact_info',
    description:
      'Get how to reach Branislav Mateáš (freelance web analyst, Brno, Czech Republic): email, LinkedIn and the contact page.',
    inputSchema: NO_INPUT,
    execute: async () =>
      text({
        name: 'Branislav Mateáš',
        role: 'Freelance web analyst and developer',
        location: 'Brno, Czech Republic',
        email: 'mateas.branislav@gmail.com',
        linkedin: 'https://www.linkedin.com/in/branislav-mateas/',
        contactPage: 'https://bmateas.com/contact',
      }),
  },
  {
    name: 'get_services',
    description:
      'List the analytics services Branislav offers, covering GA4, Google Tag Manager, dashboards, GDPR compliance and consulting.',
    inputSchema: NO_INPUT,
    execute: async () => text({ services: SERVICES, servicesPage: 'https://bmateas.com/services' }),
  },
  {
    name: 'list_blog_posts',
    description: 'List published blog posts on bmateas.com with their titles and URLs.',
    inputSchema: NO_INPUT,
    execute: async () => {
      const response = await fetch('/api/blog')
      if (!response.ok) return text('Blog posts are currently unavailable.')
      const { data } = (await response.json()) as { data?: Array<{ title: string; slug: string }> }
      return text({
        posts: (data ?? []).map(({ title, slug }) => ({ title, url: `https://bmateas.com/blog/${slug}` })),
      })
    },
  },
]

export default function WebMCPTools() {
  useEffect(() => {
    const modelContext = (navigator as Navigator & { modelContext?: ModelContext }).modelContext
    if (!modelContext?.registerTool) return

    const registrations = TOOLS.map((tool) => modelContext.registerTool(tool))

    return () => {
      for (const registration of registrations) {
        const unregister = (registration as { unregister?: () => void })?.unregister
        if (typeof unregister === 'function') unregister.call(registration)
      }
    }
  }, [])

  return null
}
